import React, { useState, useEffect, useRef } from 'react';
import { Plus, Receipt } from 'lucide-react';
import { toast } from 'react-toastify';
import { DonationsAPI, DonationTypesAPI, DonorDetails, DonorsAPI, GuestDonorsAPI } from '../../lib/sdk/index';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import { useCreateDonation } from '../../hooks/useQueries';
import { printDonationReceipt } from '../../lib/printReceipt';
import { verifyPosPrinterReady } from '../../lib/posPrinterGate';
import { getLastFormDefault, setLastFormDefault } from '../../lib/formDefaults';
import { isElectronApp } from '../../lib/isElectron';
import { getErrorMessage } from '../../lib/utils/errorMessage';

// Electron API type definitions
declare global {
  interface Window {
    electronAPI?: {
      getPrinters(): Promise<Array<{ name: string; description?: string; status?: number }>>;
      checkPosPrinter(printerName?: string): Promise<{
        available: boolean;
        printer: any;
        error: string | null;
        physicallyConnected?: boolean;
        physicalStatus?: string;
      }>;
      printReceipt(receiptData: any): Promise<{ success: boolean; error?: string; printerName?: string }>;
      printDonorReceipt(receiptData: any): Promise<{ success: boolean; error?: string; code?: string; printerName?: string }>;
      printExpenseReceipt(receiptData: any): Promise<{ success: boolean; error?: string; code?: string; printerName?: string }>;
      printIncomeReceipt(receiptData: any): Promise<{ success: boolean; error?: string; code?: string; printerName?: string }>;
      verifyPrint(printerName: string): Promise<{ success: boolean; error?: string }>;
      listUsbDevices(): Promise<{ success: boolean; devices?: any[]; potentialPrinters?: any[]; error?: string }>;
      getAllDetectedPrinters(): Promise<{ success: boolean; printers?: any[]; error?: string }>;
      addPrinterToDatabase(vendorId: number, productId: number, name: string, brand: string, model: string):
        Promise<{ success: boolean; added?: boolean; message?: string; error?: string }>;
      dbGetAllDonationTypes(): Promise<{ success: boolean; data?: any[]; error?: string }>;
    };
  }
}

interface DonationType {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
}



const AddDonation: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [formattedPreview, setFormattedPreview] = useState('');
  const [donorType, setDonorType] = useState<'donor' | 'guest'>('donor');
  const [donorDetails, setDonorDetails] = useState<DonorDetails | null>(null);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [donationTypes, setDonationTypes] = useState<DonationType[]>([]);
  const [showDonationForm, setShowDonationForm] = useState(false);

  // Print state management
  const [isPrinting, setIsPrinting] = useState(false);
  const [posPrinterAvailable, setPosPrinterAvailable] = useState(false);
  const [printerStatusMessage, setPrinterStatusMessage] = useState('');
  const printerPollPausedRef = useRef(false);

  // Create donation mutation hook
  const createDonationMutation = useCreateDonation();

  // Form state
  const [formData, setFormData] = useState({
    donationTypeId: '',
    amount: '',
    ref: ''
  });

  // Check if Electron is available
  const [electronAvailable, setElectronAvailable] = useState(false);

  useEffect(() => {
    setElectronAvailable(isElectronApp());
  }, []);

  // Load initial data
  useEffect(() => {
    loadDonationTypes();
  }, []);

  // Check POS printer availability on mount and periodically
  useEffect(() => {
    if (electronAvailable) {
      checkPrinterAvailability();
      const interval = setInterval(() => {
        if (!printerPollPausedRef.current) {
          checkPrinterAvailability();
        }
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [electronAvailable]);

  const loadDonationTypes = async () => {
    try {
      // Try local data first (offline-first)
      let typesData: DonationType[] = [];

      try {
        if (window.electronAPI?.dbGetAllDonationTypes) {
          const localTypes = await window.electronAPI.dbGetAllDonationTypes();
          if (localTypes.success && localTypes.data) {
            typesData = localTypes.data.filter((type: any) => type.isActive !== false);
          }
        }
      } catch (error) {
        console.warn('Error loading from local DB, falling back to API:', error);
      }

      // If online, try API (stale-while-revalidate)
      if (navigator.onLine && typesData.length === 0) {
        try {
          const response = await DonationTypesAPI.getAllDonationTypes();
          if (Array.isArray(response) && response.length > 0) {
            typesData = response.filter((type: DonationType) => type.isActive);
          }
        } catch (error) {
          console.warn('API call failed, using local data:', error);
        }
      }

      setDonationTypes(typesData);

      const lastTypeId = getLastFormDefault('lastDonationTypeId');
      if (lastTypeId && typesData.some((t) => t._id === lastTypeId)) {
        setFormData((prev) => ({ ...prev, donationTypeId: lastTypeId }));
      }
    } catch (error) {
      console.error('Failed to load donation types:', error);
    }
  };

  const checkPrinterAvailability = async () => {
    const gate = await verifyPosPrinterReady();
    setPosPrinterAvailable(gate.ready);
    setPrinterStatusMessage(gate.ready ? (gate.printerName || 'Connected') : gate.message);
  };



  // Auto-format input based on type
  const autoFormatInput = (value: string, type: 'cnic' | 'phone') => {
    if (type === 'cnic') {
      // Format CNIC: 12345-1234567-1
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length <= 5) return cleaned;
      if (cleaned.length <= 12) return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
      if (cleaned.length <= 13) return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 12)}-${cleaned.slice(12)}`;
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 12)}-${cleaned.slice(12, 13)}`;
    } else {
      // Format phone: +92-3338932285 (correct format)
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.startsWith('92') && cleaned.length >= 12) {
        // Already has country code
        return `+${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
      } else if (cleaned.startsWith('0') && cleaned.length >= 11) {
        // Local format, add country code
        return `+92-${cleaned.slice(1)}`;
      } else if (cleaned.length >= 11) {
        // Assume local format without leading 0
        return `+92-${cleaned}`;
      }
      return cleaned;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchValue(value);

    // Auto-detect donor type based on input format
    const trimmedValue = value.trim();

    // Auto-detect if input explicitly starts with D/d or G/g
    if (trimmedValue.toLowerCase().startsWith('d') && trimmedValue.length > 1) {
      setDonorType('donor');
    } else if (trimmedValue.toLowerCase().startsWith('g') && trimmedValue.length > 1) {
      setDonorType('guest');
    }
    // For pure numbers, keep the current donor type selection

    // Auto-format based on input type
    if (value.includes('-') && value.match(/^\d{5}-\d{7}-\d$/)) {
      setFormattedPreview(autoFormatInput(value, 'cnic'));
    } else if (value.includes('-') && value.match(/^\d{4}-\d{7}$/)) {
      setFormattedPreview(autoFormatInput(value, 'phone'));
    } else {
      setFormattedPreview(value);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchDonor();
    }
  };

  const searchDonor = async () => {
    if (!searchValue.trim()) return;

    setSearching(true);
    setHasSearched(false);
    setDonorDetails(null);

    try {
      let donor: DonorDetails | null = null;
      let searchValueToUse = searchValue.trim();

      // Auto-format the search value if it looks like raw numbers
      if (searchValueToUse.match(/^\d{13}$/)) {
        // Raw CNIC: 3330394906311 -> 33303-9490631-1
        searchValueToUse = autoFormatInput(searchValueToUse, 'cnic');
      } else if (searchValueToUse.match(/^\d{11}$/)) {
        // Raw phone: 03338932285 -> +923338-932285
        searchValueToUse = autoFormatInput(searchValueToUse, 'phone');
      }

      // Handle donor ID prefixes - normalize to uppercase and set donor type
      if (searchValueToUse.toLowerCase().startsWith('d') && searchValueToUse.length > 1 && !searchValueToUse.includes('-')) {
        // Input has D/d prefix - normalize to uppercase D and set donor type
        searchValueToUse = 'D' + searchValueToUse.substring(1);
        setDonorType('donor');
      } else if (searchValueToUse.toLowerCase().startsWith('g') && searchValueToUse.length > 1 && !searchValueToUse.includes('-')) {
        // Input has G/g prefix - normalize to uppercase G and set donor type
        searchValueToUse = 'G' + searchValueToUse.substring(1);
        setDonorType('guest');
      } else if (searchValueToUse.match(/^\d+$/) && !searchValueToUse.includes('-')) {
        // Pure number input - add appropriate prefix based on selected donor type
        if (donorType === 'donor') {
          searchValueToUse = 'D' + searchValueToUse;
        } else if (donorType === 'guest') {
          searchValueToUse = 'G' + searchValueToUse;
        }
      }

      // Determine search type and call appropriate API based on selected donor type
      if (searchValueToUse.includes('-') && searchValueToUse.match(/^\d{5}-\d{7}-\d$/)) {
        // CNIC search - use the selected donor type
        if (donorType === 'donor') {
          try {
            const response = await DonorsAPI.searchDonorByCNIC(searchValueToUse);

            // The response is the donor object directly, not wrapped in {success: true, data: {...}}
            if (response) {
              const donorData = response;

              donor = {
                id: donorData.id || donorData.donorId || donorData._id,
                donorType: 'donor' as const,
                name: donorData.name,
                fatherOrHusbandName: donorData.fatherOrHusbandName,
                relation: donorData.relation,
                address: donorData.address || '',
                cnic: donorData.cnic || searchValueToUse,
                officeId: donorData.officeId,
                officeName: donorData.officeName,
                officeNo: donorData.officeNo,
                phone: donorData.phone || '',
                email: donorData.email || ''
              };

              console.log('Mapped donor data:', donor);
            }
          } catch (error) {
            console.error('CNIC search error:', error);
            // No regular donor found with this CNIC
          }
        } else {
          // Guest donors don't have CNIC, but we can search by phone if they have one
          try {
            const response = await GuestDonorsAPI.searchGuestDonorByPhone(searchValueToUse);
            if (response && response.success && response.data) {
              // Map GuestDonor data to DonorDetails format
              const donorData = response.data;
              donor = {
                id: donorData.id || donorData.guestId || donorData._id,
                donorType: 'guestDonor' as const,
                name: donorData.name,
                fatherOrHusbandName: donorData.fatherOrHusbandName,
                relation: donorData.relation,
                address: donorData.address,
                officeId: donorData.officeId,
                officeName: donorData.officeName,
                officeNo: donorData.officeNo,
                phone: donorData.phone,
                email: donorData.email
              };
            }
          } catch {
            // No guest donor found with this CNIC
          }
        }
      } else if (searchValueToUse.includes('-') && searchValueToUse.match(/^\+92-\d{10}$/)) {
        // Phone search - try based on selected donor type
        if (donorType === 'donor') {
          try {
            const response = await DonorsAPI.searchDonorByPhone(searchValueToUse);

            // The response is the donor object directly, not wrapped in {success: true, data: {...}}
            if (response) {
              const donorData = response;

              donor = {
                id: donorData.id || donorData.donorId || donorData._id,
                donorType: 'donor' as const,
                name: donorData.name,
                fatherOrHusbandName: donorData.fatherOrHusbandName,
                relation: donorData.relation,
                address: donorData.address || '',
                cnic: donorData.cnic,
                officeId: donorData.officeId,
                officeName: donorData.officeName,
                officeNo: donorData.officeNo,
                phone: donorData.phone || '',
                email: donorData.email || ''
              };

              console.log('Mapped donor data:', donor);
            }
          } catch (error) {
            console.error('Phone search error:', error);
            // No regular donor found with this phone
          }
        } else {
          try {
            const response = await GuestDonorsAPI.searchGuestDonorByPhone(searchValueToUse);
            if (response && response.success && response.data) {
              // Map GuestDonor data to DonorDetails format
              const donorData = response.data;
              donor = {
                id: donorData.id || donorData.guestId || donorData._id,
                donorType: 'guestDonor' as const,
                name: donorData.name,
                fatherOrHusbandName: donorData.fatherOrHusbandName,
                relation: donorData.relation,
                address: donorData.address,
                officeId: donorData.officeId,
                officeName: donorData.officeName,
                officeNo: donorData.officeNo,
                phone: donorData.phone,
                email: donorData.email
              };
            }
          } catch {
            // No guest donor found with this phone
          }
        }
      } else {
        // Try as ID - search in the appropriate table based on donor type
        if (donorType === 'donor') {
          try {
            const response = await DonationsAPI.getDonorDetails(searchValueToUse, 'donor');
            // The getDonorDetails already returns data in the correct format
            donor = response;
          } catch {
            // No regular donor found with this ID
          }
        } else {
          try {
            const response = await DonationsAPI.getDonorDetails(searchValueToUse, 'guest');
            // The getDonorDetails already returns data in the correct format
            donor = response;
          } catch {
            // No guest donor found with this ID
          }
        }
      }

      if (donor) {
        setDonorDetails(donor);
        setShowDonationForm(true);
      }

      setHasSearched(true);
    } catch (error) {
      console.error('Search failed:', error);
      setHasSearched(true);
    } finally {
      setSearching(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Guard against rapid double-submits: isPrinting is only set true after an
    // awaited printer check below, leaving a small window where a second click
    // could fire a duplicate donation. Bail out immediately if already running.
    if (isPrinting || createDonationMutation.isLoading) {
      return;
    }

    if (!donorDetails || !formData.donationTypeId || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!electronAvailable) {
      toast.error(
        'Donations can only be recorded in the HRIA Desktop app with a connected POS printer. Do not use the website in a browser.',
        { autoClose: 10000 }
      );
      return;
    }

    const printerGate = await verifyPosPrinterReady();
    if (!printerGate.ready) {
      setPosPrinterAvailable(false);
      setPrinterStatusMessage(printerGate.message);
      toast.error(printerGate.message, { autoClose: 10000 });
      return;
    }
    setPosPrinterAvailable(true);
    setPrinterStatusMessage(printerGate.printerName || 'Connected');

    setIsPrinting(true);
    printerPollPausedRef.current = true;

    try {
      const donationData = {
        donorId: donorDetails.id,
        donorType: (donorType === 'donor' ? 'donor' : 'guest') as 'donor' | 'guestDonor',
        donorName: donorDetails.name,
        donorFatherOrHusbandName: donorDetails.fatherOrHusbandName,
        donorRelation: donorDetails.relation,
        donorAddress: donorDetails.address,
        donorOfficeId: donorDetails.officeId,
        donorOfficeName: donorDetails.officeName,
        donorOfficeNo: donorDetails.officeNo,
        ref: formData.ref,
        amount: parseFloat(formData.amount),
        donationTypeId: formData.donationTypeId,
        donationTypeName: donationTypes.find(t => t._id === formData.donationTypeId)?.name || '',
        cashierId: user?.userId || '',
        cashierName: user?.name || '',
        cashierRole: user?.role || '',
        deviceId: user?.deviceId || 'desktop',
        pcInfo: {
          pcName: navigator.platform,
          pcId: `PC_${Date.now()}`,
          macAddress: undefined,
          ipAddress: undefined
        }
      };

      // Step 1: Save donation to DB. The server assigns the authoritative,
      // persisted receiptId (atomic `donation_receipt` counter) and returns the
      // saved record. This is the single source of truth for the receipt id.
      const response = await createDonationMutation.mutateAsync(donationData);
      if (!response || !response._id) {
        throw new Error('Invalid server response — donation not created properly');
      }
      const savedDonationId = response._id;

      // The PRINTED receipt id MUST equal the id persisted in the DB for this
      // donation. We therefore source it from the server response — never from a
      // locally-computed/optimistic value, which previously caused the printed
      // id to diverge from what the system stored/displayed.
      if (response.receiptId === undefined || response.receiptId === null) {
        throw new Error('Server did not return a receipt ID — cannot print receipt');
      }

      const donationDataWithReceipt = {
        ...donationData,
        receiptId: response.receiptId,
        // Server-issued unguessable token — encoded into the receipt QR so the
        // printed receipt links to the public /verify page for this donation.
        verificationCode: response.verificationCode,
        donorCnic: donorDetails.cnic,
      };

      // Step 2: Print receipt (Electron only) — if print fails, rollback when possible
      if (electronAvailable) {
        try {
          const printResult = await printDonationReceipt(donationDataWithReceipt, { mandatory: true });
          if (printResult.printerName) {
            DonationsAPI.confirmPrint(savedDonationId, {
              printerName: printResult.printerName,
              printSuccess: true,
            }).catch((confirmErr) => {
              console.warn('[confirm-print] failed (non-fatal):', confirmErr);
              toast.warning('Receipt printed but printer name could not be saved on the record.');
            });
          }
          // Print confirmed — now (and only now) email the donor their receipt,
          // so a rolled-back donation never triggers an email. Best-effort.
          DonationsAPI.sendReceiptEmail(savedDonationId).catch((emailErr) => {
            console.warn('[receipt-email] send failed (non-fatal):', emailErr);
          });
        } catch (printError: unknown) {
          const printMessage = getErrorMessage(printError, 'Print failed');
          console.error('[ATOMIC] Print failed — rolling back donation:', printError);
          try {
            await DonationsAPI.deleteDonation(savedDonationId);
            console.log('[ATOMIC] Rollback succeeded — donation deleted');
          } catch (deleteError) {
            console.error('[ATOMIC] Rollback failed — donation remains in DB:', deleteError);
            throw new Error(
              `Donation #${response.receiptId} was saved and the receipt email may have been sent, but printing failed: ${printMessage}. ` +
              'Reprint from Manage Donations, or void the donation there if it should not stand.'
            );
          }
          throw new Error(
            `Receipt printing failed: ${printMessage}. The donation was not saved.`
          );
        }
      }

      toast.success(
        electronAvailable
          ? 'Donation created and receipt printed successfully!'
          : 'Donation created successfully.'
      );

      if (formData.donationTypeId) {
        setLastFormDefault('lastDonationTypeId', formData.donationTypeId);
      }

      setFormData({ donationTypeId: '', amount: '', ref: '' });
      setDonorDetails(null);
      setShowDonationForm(false);
      setSearchValue('');
      setFormattedPreview('');
      setHasSearched(false);

    } catch (error: unknown) {
      console.error('Donation creation failed:', error);
      const message = getErrorMessage(error, 'Failed to create donation');
      toast.error(message, { autoClose: 10000 });
    } finally {
      setIsPrinting(false);
      printerPollPausedRef.current = false;
      if (electronAvailable) {
        setTimeout(() => {
          checkPrinterAvailability();
        }, 2000);
      }
    }
  };

  const handleAddDonor = () => {
    if (donorType === 'donor') {
      navigate('/donors');
    } else {
      navigate('/guest-donors');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Add Donation</h1>
          <Button
            onClick={() => navigate('/donations')}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Receipt className="w-4 h-4" />
            View Donations
          </Button>
        </div>

        {!electronAvailable && (
          <div
            className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200"
            role="alert"
          >
            <p className="font-semibold">Desktop app required</p>
            <p className="mt-1 text-sm">
              Donations require the <strong>installed desktop app</strong> (Electron), not the website in a browser.
              The web UI is for admin/reports only.
            </p>
            <p className="mt-2 text-sm">
              <a
                href="https://app.hria.edu.pk/downloads"
                className="font-medium underline"
                target="_blank"
                rel="noreferrer"
              >
                Download beta desktop installer
              </a>
            </p>
          </div>
        )}

        {/* Donor Search Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Search Donor</h2>

          {/* Donor Type Selection */}
          <div className="mb-4">
            <div className="flex items-center gap-4 mb-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="donorType"
                  value="donor"
                  checked={donorType === 'donor'}
                  onChange={(e) => setDonorType(e.target.value as 'donor' | 'guest')}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Regular Donor</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="donorType"
                  value="guest"
                  checked={donorType === 'guest'}
                  onChange={(e) => setDonorType(e.target.value as 'donor' | 'guest')}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Guest Donor</span>
              </label>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <strong>Regular Donor:</strong> Search by ID (D001), CNIC, or Phone •
              <strong>Guest Donor:</strong> Search by ID (G001) or Phone only
              <br />
              <span className="text-xs text-gray-500">
                Phone format: +92-3338932285 • CNIC format: 33303-9490631-1
              </span>
            </div>
          </div>

          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <Input
                label="Search by ID, CNIC, or Phone"
                value={searchValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Enter ID (D001, G001), CNIC (12345-1234567-1), or Phone (+92-3338932285)"
                disabled={searching}
              />
              {formattedPreview && formattedPreview !== searchValue && (
                <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Formatted: {formattedPreview}
                </div>
              )}
            </div>
            <Button
              onClick={searchDonor}
              disabled={!searchValue.trim() || searching}
              className="mt-6"
            >
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </div>

          {hasSearched && !donorDetails && (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400 mb-4">
                No {donorType === 'donor' ? 'regular donor' : 'guest donor'} found with the provided information.
                <br />
                <span className="text-sm">
                  Make sure you've selected the correct donor type and entered a valid ID, CNIC, or phone number.
                </span>
              </div>
              <Button
                onClick={handleAddDonor}
                variant="outline"
                className="flex items-center gap-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                Add New {donorType === 'donor' ? 'Regular Donor' : 'Guest Donor'}
              </Button>
            </div>
          )}
        </div>

        {/* Donor Details Display */}
        {donorDetails && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Donor Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 font-medium">
                  {donorDetails.name}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {donorDetails.relation === 'Husband' ? 'Husband' : 'Father'}
                </label>
                <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 font-medium">
                  {donorDetails.fatherOrHusbandName}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Office
                </label>
                <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 font-medium">
                  {donorDetails.officeName}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Office No
                </label>
                <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 font-medium">
                  #{donorDetails.officeNo}
                </div>
              </div>

              {donorDetails.address && (
                <div className="space-y-2 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Address
                  </label>
                  <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 font-medium">
                    {donorDetails.address}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Donation Form */}
        {showDonationForm && donorDetails && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Donation Details</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Donation Type *
                  </label>
                  <select
                    name="donationTypeId"
                    value={formData.donationTypeId}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="" className="text-gray-500 dark:text-gray-400">Select Donation Type</option>
                    {donationTypes.map(type => (
                      <option key={type._id} value={type._id} className="text-gray-900 dark:text-white">
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Amount (Rs.) *
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleFormChange}
                    required
                    min="1"
                    step="1"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Reference
                  </label>
                  <input
                    type="text"
                    name="ref"
                    value={formData.ref}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Optional reference"
                  />
                </div>


              </div>

              <div className="flex justify-end gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDonationForm(false)}
                  disabled={isPrinting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isPrinting || !electronAvailable || !posPrinterAvailable}
                  className="flex items-center gap-2"
                >
                  {isPrinting ? (
                    <>
                      {isPrinting ? 'Processing...' : 'Printing Receipt...'}
                    </>
                  ) : (
                    <>
                      <Receipt className="w-4 h-4" />
                      Create Donation
                    </>
                  )}
                </Button>
              </div>

              {/* Printer status — donations are blocked until hardware is connected */}
              {electronAvailable && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                  {posPrinterAvailable ? (
                    <div className="text-sm text-green-600 dark:text-green-400">
                      ✓ POS printer connected{printerStatusMessage && printerStatusMessage !== 'Connected' ? ` (${printerStatusMessage})` : ''}
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-red-600 dark:text-red-400">
                        ⚠ {printerStatusMessage || 'POS printer not connected — check USB/power and Settings → Printer'}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          toast.info('Rechecking printer connection...');
                          await checkPrinterAvailability();
                          if (posPrinterAvailable) {
                            toast.success('Printer connected!');
                          } else {
                            toast.info('No POS printer found. Configure one in Settings → Printer.');
                          }
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline whitespace-nowrap"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddDonation;
